// @flow
import * as React from 'react';
import type { RouterHistory } from 'react-router-dom';
import type { Authentication, PasswordPolicyRule } from '../../types';
import api from '../../services/api';
import createPolicyRules from './Policy/createPolicyRules';
import BusyIndicator from '../Common/BusyIndicator';
import PasswordResetPageDisplay from './PasswordResetPageDisplay';
import withAuthentication from '../withAuthentication';

class PasswordResetPage extends React.PureComponent<
  {
    authentication: Authentication,
    history: RouterHistory
  },
  {
    rules: Array<PasswordPolicyRule>,
    oldPassword: string,
    newPassword: string,
    passwordConfirmation: string,
    isNewPasswordValid: boolean,
    showPassword: boolean,
    busy: boolean,
    errors: Array<string>
  }
> {
  state = {
    rules: [],
    oldPassword: '',
    newPassword: '',
    passwordConfirmation: '',
    isNewPasswordValid: false,
    showPassword: false,
    busy: true,
    errors: []
  };

  componentDidMount() {
    api.auth
      .passwordPolicy()
      .then(response =>
        this.setState({ rules: createPolicyRules(response.data), busy: false })
      )
      .catch(error => this.setState({ busy: false }));
  }

  handleChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
    const { name, value } = event.currentTarget;
    let isNewPasswordValid = this.state.isNewPasswordValid;

    if (name === 'newPassword') {
      isNewPasswordValid = this.state.rules.every(rule => rule.validate(value));
    }

    this.setState({ [name]: value, isNewPasswordValid });
  };

  handleChangeShowPassword = (): void => {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  };

  handleCancel = () => {
    this.props.authentication.logout();
    this.props.history.replace('/');
  };

  handleSubmit = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const {
      oldPassword,
      newPassword,
      passwordConfirmation,
      isNewPasswordValid
    } = this.state;

    let errors = [];

    if (oldPassword.trim().length === 0) {
      errors = ['Please enter your old password'];
    } else if (newPassword.trim().length === 0) {
      errors = ['Please enter a new password'];
    } else if (!isNewPasswordValid) {
      errors = ['Your new password does not meet the specified requirements'];
    } else if (passwordConfirmation !== newPassword) {
      errors = ['Your new and confirm passwords do not match'];
    } else if (oldPassword === newPassword) {
      errors = ['Your old and new passwords must be different'];
    }

    if (errors.length > 0) {
      this.setState({ errors });
    } else {
      api.auth
        .updatePassword(this.state.oldPassword, this.state.newPassword)
        .then(() => {
          this.props.authentication.handlePasswordReset();
          this.props.history.replace('/');
        })
        .catch(errors => this.setState({ errors }));
    }
  };

  render() {
    if (this.state.busy) {
      return <BusyIndicator size="5x" />;
    }

    const {
      rules,
      oldPassword,
      newPassword,
      passwordConfirmation,
      showPassword,
      isNewPasswordValid,
      errors
    } = this.state;

    return (
      <PasswordResetPageDisplay
        oldPassword={oldPassword}
        newPassword={newPassword}
        passwordConfirmation={passwordConfirmation}
        showPassword={showPassword}
        isNewPasswordValid={isNewPasswordValid}
        rules={rules}
        errors={errors}
        onChange={this.handleChange}
        onChangeShowPassword={this.handleChangeShowPassword}
        onCancel={this.handleCancel}
        onSubmit={this.handleSubmit}
      />
    );
  }
}

export default withAuthentication(PasswordResetPage);
